import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Package, Truck, Trophy, Store, MapPin, AlertCircle, ExternalLink } from "lucide-react";
import { api, fileUrl, trackingUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader } from "@/components/common";

export default function Orders() {
  const { user } = useAuth();
  const [orders, setOrders] = useState(null);
  const load = useCallback(() => api.get("/orders").then(({ data }) => setOrders(data)).catch(() => setOrders([])), []);
  useEffect(() => { load(); }, [load]);

  if (orders === null) return <Loader />;
  const hasAddress = user?.full_name && user?.address;

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
      <h1 className="font-display text-3xl font-black text-white">My Orders</h1>
      <p className="text-slate-400 mt-1">Prizes you've won and items you've bought.</p>

      <div className="mt-4 card-surface rounded-2xl p-4 flex items-start gap-3" data-testid="shipping-summary">
        <MapPin className="w-5 h-5 text-gold shrink-0 mt-0.5" />
        {hasAddress ? (
          <div className="flex-1">
            <p className="text-sm text-white font-semibold">Shipping to {user.full_name}</p>
            <p className="text-sm text-slate-400 whitespace-pre-line">{user.address}</p>
            <Link to="/account" className="text-xs text-gold mt-1 inline-block">Edit in Account →</Link>
          </div>
        ) : (
          <div className="flex-1">
            <p className="text-sm text-amber-400 font-semibold flex items-center gap-1"><AlertCircle className="w-4 h-4" /> No shipping address saved</p>
            <Link to="/account" className="text-xs text-gold mt-1 inline-block">Add your real name & address →</Link>
          </div>
        )}
      </div>

      {orders.length === 0 ? (
        <div className="card-surface rounded-2xl p-16 text-center text-slate-400 mt-6"><Package className="w-10 h-10 mx-auto text-gold/40 mb-3" />No orders yet.</div>
      ) : (
        <div className="mt-6 space-y-4" data-testid="orders-list">
          {orders.map((o) => {
            const shipped = o.status === "shipped";
            return (
              <div key={o.id} className="card-surface rounded-2xl p-4" data-testid={`order-${o.id}`}>
                <div className="flex gap-4">
                  <div className="w-16 h-16 rounded-xl bg-vault overflow-hidden shrink-0">{o.item_photo ? <img src={fileUrl(o.item_photo)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-slate-600">{o.type === "drop_win" ? <Trophy className="w-6 h-6" /> : <Store className="w-6 h-6" />}</div>}</div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`text-[10px] font-mono font-bold px-2 py-0.5 rounded ${o.type === "drop_win" ? "bg-gold/15 text-gold" : "bg-slate-500/15 text-slate-300"}`}>{o.type === "drop_win" ? "DROP WIN" : "SHOP"}</span>
                      <span className={`text-[10px] font-mono font-bold px-2 py-0.5 rounded flex items-center gap-1 ${shipped ? "bg-emerald-400/15 text-emerald-400" : "bg-amber-400/15 text-amber-400"}`}>{shipped ? <><Truck className="w-3 h-3" /> SHIPPED</> : "PENDING"}</span>
                    </div>
                    <h3 className="font-display font-bold text-white mt-1.5">{o.item_title}</h3>
                    {o.seat && <p className="text-xs font-mono text-gold">Winning seat #{o.seat}</p>}
                    {shipped && o.tracking_number && <p className="text-sm text-slate-300 mt-1">Tracking: <a href={trackingUrl(o.tracking_number)} target="_blank" rel="noopener noreferrer" className="font-mono text-gold hover:text-gold-bright underline underline-offset-2 inline-flex items-center gap-1" data-testid={`tracking-link-${o.id}`}>{o.tracking_number} <ExternalLink className="w-3 h-3" /></a></p>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
